import { Box, Button, Divider } from '@pulse/ui';
import type { ReactNode } from 'react';

import { useFloatingPanel } from './FloatingPanelContext';

export interface NotificationPanelFooterProps {
  label?: string;
  onViewAll?: () => void;
  children?: ReactNode;
}

// No react-router import here — packages/ui stays router-agnostic (see
// packages/ui/CLAUDE.md). The default "See all" action is wired up by the caller via
// `onViewAll`; if neither that nor `children` is given, the footer renders nothing rather
// than a dead link.
export function NotificationPanelFooter({
  label = 'See all notifications →',
  onViewAll,
  children,
}: NotificationPanelFooterProps) {
  const { collapse } = useFloatingPanel();

  if (!children && !onViewAll) {
    return null;
  }

  // Navigating to the full Notifications page while the panel stays expanded on top of it
  // reads as broken — collapse it back to its header/FAB state first, then let the caller's
  // onViewAll perform the actual navigation.
  function handleViewAll() {
    collapse();
    onViewAll?.();
  }

  return (
    <>
      <Divider />
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.paper', display: 'flex', justifyContent: 'center' }}>
        {children ?? (
          <Button
            size="small"
            onClick={handleViewAll}
            variant="text"
            sx={{ fontSize: '0.8rem', fontWeight: 500, textTransform: 'none' }}
          >
            {label}
          </Button>
        )}
      </Box>
    </>
  );
}
